import { useState, useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import PageLayout from "../../Layouts/PageLayout/PageLayout";
import TabsNavigation from "../../components/common/TabsNavigation/TabsNavigation";
import Button from "../../components/common/Button/Button";
import { FiDownload, FiFileText } from "react-icons/fi";
import CalendarioTab from "../../components/layout/Gestores/CalendarioTab/CalendarioTab";
import { getApiInstance } from "../../utils/api";
import { nombreFicheroDescarga } from "../../utils/descargas";
import { CiCircleCheck, CiCircleQuestion, CiClock1 } from "react-icons/ci";
import { CgDanger } from "react-icons/cg";
import Table from "../../components/common/Table/Table";
import MapComponent from "../../components/common/MapComponent/MapComponent";
import Modal from "../../components/common/Modal/Modal";
import Badge from "../../components/common/Badge/Badge";
import { useZone } from "../../context/ZoneContext";
import { ZONE_LABEL_MAP } from "../../constants/zones";
import "./Gestores.scss";
import { mapList } from "../../utils/utils";
import { FaCheckCircle, FaClock, FaQuestionCircle } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import { IoIosAlert, IoIosCloseCircle, IoMdAdd } from "react-icons/io";

const columnasGestores = [
    { key: "codProveedor", label: "ID", visible: false },
    {
        key: "nombre",
        label: "Nombre",
        visible: true,
        sortable: true,
        render: (row) => {
            const href = `/gestores/${row.codProveedor}`;

            let ubicacion = "";
            if (row.localidad && row.pais) {
                ubicacion = `${row.localidad}, ${row.pais}`;
            } else if (row.localidad) {
                ubicacion = row.localidad;
            } else if (row.pais) {
                ubicacion = row.pais;
            }

            return (
                <Link to={href}>
                    <div className="table-cell-content">
                        <span className="table-cell-content__primary table-cell-content__primary--bold">
                            {row.nombre}
                        </span>
                        {ubicacion && (
                            <span className="table-cell-content__secondary">
                                {ubicacion}
                            </span>
                        )}
                    </div>
                </Link>
            );
        },
    },
    {
        key: "cif",
        label: "CIF",
        visible: true,
        sortable: true,
        visibleOnExpand: true,
    },
    { key: "localidad", label: "Localidad", visible: true, sortable: true, visibleOnExpand: true },
    {
        key: "contacto",
        label: "Contacto",
        visible: true,
        sortable: true,
        width: "18%",
        visibleOnExpand: true,
        render: (row) => {
            return (
                <div className="table-cell-content">
                    <span className="table-cell-content__primary">
                        {row.contacto || "-"}
                    </span>
                    <span className="table-cell-content__secondary">
                        {row.eMail || ""}
                    </span>
                </div>
            );
        },
    },
    {
        key: "telefono",
        label: "Teléfono",
        visible: true,
        sortable: true,
        visibleOnExpand: true,
    },
    { key: "eMail", label: "Email", visible: true, sortable: true, visibleOnExpand: true },
    {
        key: "codEstado",
        label: "Estado",
        visible: true,
        sortable: true,
        visibleOnExpand: true,
    },
];

const GestorResiduos = ({ codes, residuos }) => {
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 0 });
    const countRef = useRef(null);
    const popupRef = useRef(null);

    useEffect(() => {
        if (!open) return;

        const handleClickOutside = (e) => {
            if (
                popupRef.current &&
                !popupRef.current.contains(e.target) &&
                countRef.current &&
                !countRef.current.contains(e.target)
            ) {
                setOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () =>
            document.removeEventListener("mousedown", handleClickOutside);
    }, [open]);

    if (!codes.length) return "-";

    const labels = residuos
        .filter((r) => codes.includes(Number(r.value)))
        .map((r) => r.label);

    const names = labels.length ? labels : codes.map(String);

    const togglePopup = (e) => {
        e.stopPropagation();
        if (open) {
            setOpen(false);
            return;
        }
        if (countRef.current) {
            const rect = countRef.current.getBoundingClientRect();
            const popupHeight = names.length * 18 + 20;
            const spaceBelow = window.innerHeight - rect.bottom;
            const openAbove = spaceBelow < popupHeight;

            setPos({
                top: openAbove
                    ? rect.top - popupHeight - 8
                    : rect.bottom + 8,
                left: rect.left + rect.width / 2,
                openAbove,
            });
        }
        setOpen(true);
    };

    return (
        <span className="gestor-residuos">
            <span
                ref={countRef}
                className="gestor-residuos__count"
                onClick={togglePopup}
            >
                {codes.length}
            </span>
            {open && (
                <span
                    ref={popupRef}
                    className={`gestor-residuos__popup ${
                        pos.openAbove ? "gestor-residuos__popup--above" : ""
                    }`}
                    style={{
                        top: pos.top,
                        left: pos.left,
                    }}
                >
                    {names.map((item, i) => (
                        <span key={i} className="gestor-residuos__item">
                            {item}
                        </span>
                    ))}
                </span>
            )}
        </span>
    );
};

export default function Gestores({ user }) {
    const { t } = useTranslation();
    const tableRef = useRef(null);
    const canAdd = Number(user.codAcceso) === 1 && Number(user.role) !== 4;
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState("listado");
    const [gestores, setGestores] = useState([]);
    const [certificados, setCertificados] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { selectedZone } = useZone();
    const [comunidades, setComunidades] = useState([]);
    const [paises, setPaises] = useState([]);
    const [provincias, setProvincias] = useState([]);
    const [residuos, setResiduos] = useState([]);
    const [selectedProveedores, setSelectedProveedores] = useState(new Set());

    const columnasListado = useMemo(() => [
        ...columnasGestores,
        {
            key: "residuos",
            label: "Residuos",
            visible: true,
            sortable: false,
            visibleOnExpand: true,
            render: (row) => (
                <GestorResiduos
                    codes={
                        Array.isArray(row.codsResiduos)
                            ? row.codsResiduos
                            : []
                    }
                    residuos={residuos}
                />
            ),
            },
        ],
        [residuos]
    );

    const fetchGestores = async () => {
        try {
            const api = getApiInstance();
            const response = await api.get(
                `/api/Proveedores/GetProveedores`
            );
            setGestores(response.data);
            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar los gestores. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
        }
    };

    const fetchCertificados = async () => {
        try {
            const api = getApiInstance();
            const response = await api.get(
                `/api/ProveedoresCentros/GetAllCertificados`
            );
            setCertificados(response.data);
        } catch (err) {
            console.error("Error al cargar los certificados:", err);
        }
    };

    const filterFields = [
        { key: "nombre", label: t("fields.name"), type: "multiselect" },
        {
            key: "codEstado",
            label: t("fields.state"),
            type: "multiselect",
            labelMap: [
                t("states.noData"),
                t("states.pending"),
                t("states.incident"),
                t("states.validated"),
                t("states.expired"),
            ],
        },
        { key: "cif", label: t("fields.cif"), type: "text" },
        {
            key: "pais",
            label: t("fields.country"),
            type: "multiselect",
            advanced: true,
        },
        {
            key: "comunidad",
            label: t("fields.community"),
            type: "multiselect",
            advanced: true,
        },
        {
            key: "provincia",
            label: t("fields.province"),
            type: "multiselect",
            advanced: true,
        },
        {
            key: "codZona",
            label: "Zona",
            type: "multiselect",
            labelMap: ZONE_LABEL_MAP,
        },
        {
            key: "isGestorFinal",
            label: t("managers.form.finalManager"),
            type: "boolean",
            advanced: true,
            labelMap: [
                t("common.no", "No"),
                t("common.yes", "Sí"),
            ],
        },
        {
            key: "isTransportista",
            label: t("managers.form.carrier"),
            type: "boolean",
            advanced: true,
            labelMap: [
                t("common.no", "No"),
                t("common.yes", "Sí"),
            ],
        },
    ];

    const fetchComunidades = async () => {
        const api = getApiInstance();

        try {
            let comunidadesData = [];

            const response = await api.get(
                `/api/Comunidades/GetComunidades`
            );
            comunidadesData = response.data;

            setComunidades([
                ...mapList(
                    comunidadesData,
                    "comunidad",
                    "codComunidad",
                    "codComunidad",
                    "codPais"
                ),
            ]);

            console.log("Comunidades data fetched:", comunidadesData);

            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar las comunidades. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
        }
    };

    const fetchPaises = async () => {
        const api = getApiInstance();

        try {
            let paisesData = [];

            const response = await api.get(
                `/api/Paises/GetPaises`
            );
            paisesData = response.data;

            setPaises(mapList(paisesData, "pais", "codPais", "codPais"));

            console.log("Paises data fetched:", paisesData);

            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar los países. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
        }
    };

    const fetchProvincias = async () => {
        const api = getApiInstance();
        try {
            let provinciasData = [];

            const response = await api.get(
                `/api/Provincias/GetProvincias`
            );
            provinciasData = response.data;

            setProvincias([
                ...mapList(
                    provinciasData,
                    "provincia",
                    "codProvincia",
                    "codProvincia",
                    "codComunidad"
                ),
            ]);
            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar las provincias. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
        }
    };

    const fetchResiduos = async () => {
        const api = getApiInstance();
        try {
            let residuosData = [];

            const response = await api.get(
                `/api/Residuos/GetAllResiduos`
            );
            residuosData = response.data;

            setResiduos([
                ...mapList(residuosData, "nombre", "codResiduo", "codResiduo"),
            ]);
            setError(null);
        } catch (err) {
            setError(
                "No se han podido cargar los residuos. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
        }
    };

    useEffect(() => {
        let isMounted = true;
        setLoading(true);
        setError(null);

        const loadAll = async () => {
            try {
                await fetchGestores();

                await Promise.all([
                    fetchCertificados(),
                    fetchPaises(),
                    fetchComunidades(),
                    fetchProvincias(),
                    fetchResiduos(),
                ]);
            } catch (err) {
                if (isMounted) {
                    setError(
                        "No se han podido cargar los datos. Vuelve a intentarlo más tarde."
                    );
                }
                console.error("Error en loadAll:", err);
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        loadAll();

        return () => {
            isMounted = false;
        };
    }, []);

    const tabs = [
        { id: "listado", label: "Listado" },
        { id: "mapa", label: "Mapa" },
        { id: "calendario", label: "Calendario" },
    ];

    const handleTabChange = (tabId) => {
        setActiveTab(tabId);
    };

    const modalMap = (gestor) => {
        const status = getComplianceStatus(gestor);
        return (
            <>
                <div className="gestor-map-details__info">
                    <div className="info-row">
                        <strong>{t("fields.name")}:</strong> {gestor.nombre}
                    </div>
                    <div className="info-row">
                        <strong>{t("fields.contact")}:</strong>{" "}
                        {gestor.contacto || t("common.noResults")}
                    </div>
                    <div className="info-row">
                        <strong>{t("fields.phone")}:</strong>{" "}
                        {gestor.telefono || t("common.noResults")}
                    </div>
                    <div className="info-row">
                        <strong>{t("fields.email")}:</strong>{" "}
                        {gestor.eMail || t("common.noResults")}
                    </div>
                    <div className="info-row">
                        <strong>Autorización:</strong> {gestor.cif}
                    </div>
                </div>
                <div className="gestor-map-details__compliance">
                    <div className="compliance-header">
                        <h4 className="compliance-title">
                            {t("sections.legalCompliance")}
                        </h4>
                        <Badge variant={status.type} Icon={status.Icon}>
                            {status.label}
                        </Badge>
                    </div>
                    <div className="gestor-map-details">
                        <h3 className="modal-title">{gestor.nombre}</h3>
                        <p className="gestor-location">{gestor.localidad}</p>

                        <div className="gestor-map-details__info">
                            <div className="info-row">
                                <strong>Gestor:</strong> {gestor.nombre}
                            </div>
                            <div className="info-row">
                                <strong>Contacto:</strong>{" "}
                                {gestor.contacto || "No especificado"}
                            </div>
                            <div className="info-row">
                                <strong>Email:</strong>{" "}
                                {gestor.eMail || "No especificado"}
                            </div>
                            <div className="info-row">
                                <strong>Teléfono:</strong>{" "}
                                {gestor.telefono || "No especificado"}
                            </div>
                            <div className="info-row">
                                <strong>Autorización:</strong> {gestor.cif}
                            </div>
                        </div>
                        <div className="modal-actions">
                            <Button
                                variant="filled"
                                onClick={() => {
                                    navigate(`/gestores/${gestor.codProveedor}`);
                                }}
                            >
                                {t("common.view")} Ficha Completa
                            </Button>
                        </div>
                    </div>
                </div>
            </>
        );
    };

    const getComplianceStatus = (gestor) => {
        switch (gestor.codEstado) {
            case 0:
                return { label: "Sin datos", type: "info", Icon: CiClock1 };
            case 1:
                return {
                    label: "Pendiente",
                    type: "info",
                    Icon: CiClock1,
                };
            case 2:
                return {
                    label: "Incidencia",
                    type: "warning",
                    Icon: CgDanger,
                };
            case 3:
                return {
                    label: "Validada",
                    type: "success",
                    Icon: CiCircleCheck,
                };
            case 4:
                return {
                    label: "Expirado",
                    type: "danger",
                    Icon: CiCircleCheck,
                };
            default:
                return { label: "Sin estado", type: "outlined" };
        }
    };

    const renderTabContent = () => {
        switch (activeTab) {
            case "listado":
                return (
                    <Table
                        ref={tableRef}
                        hideAddButton={true}
                        columns={columnasListado}
                        dataTable={gestores}
                        keyProp="codProveedor"
                        sortProp="nombre"
                        showCheckboxes={true}
                        expandableBreakpoint={600}
                        expandable={true}
                        noResultsMessage={t("managers.noManagersFound")}
                        actions={[
                            t("common.delete"),
                            {
                                label: t("common.managerRecord"),
                                icon: <FiFileText size={16} />,
                                action: (row) =>
                                    navigate(
                                        `/gestores/${row.codProveedor}`
                                    ),
                            },
                            t("common.edit"),
                        ]}
                        searchTerms={["nombre", "cif", "localidad", "contacto", "telefono", "eMail"]}
                        filterFields={filterFields}
                        elementName={t("managers.title")}
                        stateProp="codEstado"
                        states={[
                            {
                                label: t("states.noData"),
                                type: "info",
                                Icon: CiClock1,
                            },
                            {
                                label: t("states.pending"),
                                type: "info",
                                Icon: CiClock1,
                            },
                            {
                                label: t("states.incident"),
                                type: "warning",
                                Icon: CgDanger,
                            },
                            {
                                label: t("states.validated"),
                                type: "success",
                                Icon: CiCircleCheck,
                            },
                            {
                                label: t("states.expired"),
                                type: "danger",
                                Icon: IoIosAlert,
                            },
                        ]}
                        formElements={[
                            {
                                title: t("sections.generalInfo"),
                                titleVisible: true,
                                elements: [
                                    {
                                        name: "nombre",
                                        type: "text",
                                        label: t("fields.name"),
                                        placeholder: t(
                                            "placeholders.enterName"
                                        ),
                                        required: true,
                                        disabled: false,
                                        labelVisible: true,
                                        visible: true,
                                    },
                                    {
                                        name: "cif",
                                        type: "text",
                                        label: t("fields.cif"),
                                        placeholder: t("placeholders.enterCif"),
                                        required: true,
                                        disabled: false,
                                        labelVisible: true,
                                        visible: true,
                                    },
                                    {
                                        name: "contacto",
                                        type: "text",
                                        label: t("fields.contact"),
                                        placeholder: t(
                                            "placeholders.enterName"
                                        ),
                                        required: false,
                                        disabled: false,
                                        labelVisible: true,
                                        visible: true,
                                    },
                                    {
                                        name: "telefono",
                                        type: "tel",
                                        label: t("fields.phone"),
                                        placeholder: t(
                                            "placeholders.enterPhone"
                                        ),
                                        required: false,
                                        disabled: false,
                                        labelVisible: true,
                                        visible: true,
                                    },
                                    {
                                        name: "eMail",
                                        type: "email",
                                        label: t("fields.email"),
                                        placeholder: t(
                                            "placeholders.enterEmail"
                                        ),
                                        required: false,
                                        disabled: false,
                                        labelVisible: true,
                                        visible: true,
                                    },
                                    {
                                        name: "codsResiduos",
                                        type: "multiselect",
                                        options: residuos,
                                        label: t("managers.form.wasteTypes"),
                                        placeholder: t(
                                            "managers.form.wasteTypesPlaceholder"
                                        ),
                                        required: false,
                                        disabled: false,
                                        labelVisible: true,
                                        visible: true,
                                        withCheckbox: true,
                                    },
                                    {
                                        name: "isGestorFinal",
                                        type: "checkbox",
                                        label: t("managers.form.finalManager"),
                                        required: false,
                                        disabled: false,
                                        labelVisible: true,
                                        visible: true,
                                        defaultValue: false,
                                    },
                                    {
                                        name: "isTransportista",
                                        type: "checkbox",
                                        label: t("managers.form.carrier"),
                                        required: false,
                                        disabled: false,
                                        labelVisible: true,
                                        visible: true,
                                        defaultValue: false,
                                    },
                                    
                                ],
                            },
                            {
                                title: t("sections.location"),
                                titleVisible: true,
                                elements: [
                                    {
                                        name: "direccion",
                                        type: "text",
                                        label: t("fields.address"),
                                        placeholder: t(
                                            "placeholders.enterAddress"
                                        ),
                                        required: true,
                                        disabled: false,
                                        labelVisible: true,
                                        visible: true,
                                    },
                                    {
                                        name: "localidad",
                                        type: "text",
                                        label: t("fields.locality"),
                                        placeholder: t(
                                            "placeholders.enterLocality"
                                        ),
                                        required: true,
                                        disabled: false,
                                        labelVisible: true,
                                        visible: true,
                                    },
                                    {
                                        name: "cp",
                                        type: "text",
                                        label: t("fields.postalCode"),
                                        placeholder: t(
                                            "placeholders.enterPostalCode"
                                        ),
                                        required: false,
                                        disabled: false,
                                        labelVisible: true,
                                        visible: true,
                                    },
                                    {
                                        name: "codPais",
                                        type: "select",
                                        label: t("fields.country"),
                                        placeholder: t(
                                            "placeholders.selectCountry"
                                        ),
                                        options: paises,
                                        required: true,
                                        disabled: false,
                                        labelVisible: true,
                                        visible: true,
                                    },
                                    {
                                        name: "codComunidad",
                                        type: "select",
                                        label: t("fields.community"),
                                        placeholder: t(
                                            "placeholders.selectCommunity"
                                        ),
                                        options: comunidades,
                                        required: true,
                                        disabled: false,
                                        labelVisible: true,
                                        visible: true,
                                        associatedFormElement: "codPais",
                                    },
                                    {
                                        name: "codProvincia",
                                        type: "select",
                                        label: t("fields.province"),
                                        placeholder: t(
                                            "placeholders.selectProvince"
                                        ),
                                        options: provincias,
                                        required: true,
                                        disabled: false,
                                        labelVisible: true,
                                        visible: true,
                                        associatedFormElement: "codComunidad",
                                    },
                                ],
                            },
                        ]}
                        loading={loading}
                        addApiUrl="/api/Proveedores/SetProveedor"
                        editApiUrl="/api/Proveedores/EditProveedor"
                        deleteApiUrl="/api/Proveedores/DeleteProveedor"
                        selectedRows={selectedProveedores}
                        onSelectionChange={setSelectedProveedores}
                        userCodAcceso={Number(user.codAcceso)}
                        userCodPerfil={Number(user.role)}
                    />
                );
            case "mapa":
                return (
                    <MapComponent
                        data={gestores}
                        center={[42.0, 2.0]}
                        zoom={5}
                        height="600px"
                        width="100%"
                        modalContent={modalMap}
                    />
                );
            case "calendario":
                return <CalendarioTab gestoresData={certificados} />;
            default:
                return null;
        }
    };

    const handleExportarSeleccionados = () => {
        let filasAExportar;

        if (selectedProveedores && selectedProveedores.size > 0) {
            // Si hay filas marcadas, exportar solo esas.
            filasAExportar = (gestores ?? []).filter((g) =>
                [...selectedProveedores].some(
                    (id) => String(id) === String(g.codProveedor)
                )
            );
        } else {
            // Si no hay seleccion, exportar las filas que muestra la tabla
            // (respetando los filtros aplicados, incluido el de Zona).
            filasAExportar = tableRef.current?.getVisibleRows() ?? [];
        }

        if (filasAExportar.length === 0) {
            setError(
                "No hay gestores que exportar con los filtros aplicados."
            );
            return;
        }

        const codsProveedores = filasAExportar.map(
            (item) => item.codProveedor
        );

        generateExcel({ codsProveedores });
    };

    const [exportando, setExportando] = useState(false);

    const generateExcel = async ({ codsProveedores }) => {
        if (exportando) return;
        setExportando(true);
        const api = getApiInstance();
        const codZona = selectedZone.value;
        const codZonaLegal = 1;

        try {
            const response = await api.post(
                "/api/Proveedores/CreateReportByCods",
                codsProveedores,
                {
                    responseType: "blob",
                }
            );

            const fileName = nombreFicheroDescarga(
                response.headers["content-disposition"],
                "Informe_Proveedores.xlsx"
            );

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setError(
                "No se pudo generar el reporte. Vuelve a intentarlo más tarde."
            );
            console.error(err);
        } finally {
            setExportando(false);
        }
    };

    return (
        <PageLayout
            title={t("managers.title")}
            subtitle={t("managers.subtitle")}
            actions={[
                ...(canAdd
                    ? [
                          <Button
                              key="add"
                              variant="filled"
                              startIcon={IoMdAdd({})}
                              onClick={() => tableRef.current?.openAddModal()}
                              endIcon={undefined}
                          >
                              {`${t("common.add")} ${t("managers.title")}`}
                          </Button>,
                      ]
                    : []),
                <Button
                    key="exportar"
                    variant="filled"
                    startIcon={FiDownload({})}
                    onClick={handleExportarSeleccionados}
                    endIcon={undefined}
                    disabled={exportando}
                >
                    {exportando ? "Generando..." : t("managers.exportManagers")}
                </Button>,
            ]}
        >
            <TabsNavigation
                tabs={tabs}
                activeTab={activeTab}
                onTabChange={handleTabChange}
            />
            {error && (
                <div
                    style={{
                        padding: "20px",
                        textAlign: "center",
                        fontSize: "16px",
                        color: "#e74c3c",
                        backgroundColor: "#fdf2f2",
                        border: "1px solid #fecaca",
                        borderRadius: "6px",
                        margin: "10px 0",
                    }}
                >
                    {error}
                </div>
            )}

            {!error && renderTabContent()}
        </PageLayout>
    );
}
